import { useState } from 'react';
import { Trans, useLingui } from '@lingui/react/macro';

import Box from '@/design_system/Box';
import Button from '@/design_system/Button';
import Dialog from '@/design_system/Dialog';
import { Footer, FooterButtons } from '@/design_system/Footer';
import { TranslatableInputText } from '@/design_system/InputText';
import Message from '@/design_system/Message';
import Stack from '@/design_system/Stack';
import Toggle from '@/design_system/Toggle';
import Paragraph from '@/design_system/Typography/Paragraph';
import Title from '@/design_system/Typography/Title';
import { type Product, useUpdateProduct } from '@/models/product';
import { AddValueButton } from '@/routes/SettingsBeta/components/AddValueButton';
import { DeleteButtonIconOnly } from '@/routes/SettingsBeta/components/DeleteButtonIconOnly';
import {
  EditButton,
  SaveCancelButtons,
} from '@/routes/SettingsBeta/components/SaveEditCancelButtons';
import { I18nWording, Locale } from '@/services/i18n';

export const SizesSection = ({ product }: { product: Product }) => {
  const { t } = useLingui();
  const { mutateAsync: updateProduct, isPending: isUpdateProductPending } = useUpdateProduct(
    product.id
  );
  const [isEditing, setIsEditing] = useState(false);
  const [sizes, setSizes] = useState<I18nWording[] | null>(product.sizes);
  const [isDeleteSizesModalOpen, setIsDeleteSizesModalOpen] = useState(false);
  const [showError, setShowError] = useState(false);

  const handleSave = async () => {
    if (sizes?.length && sizes.some((size) => !size.en || !size.fr)) {
      setShowError(true);

      return;
    }

    await updateProduct({ sizes });
    setIsEditing(false);
    setShowError(false);
  };

  const handleCancel = () => {
    setShowError(false);
    setIsEditing(false);
    setSizes(product.sizes);
  };

  return (
    <Box>
      <Stack gap="0.5rem">
        <Stack row justifyContent="space-between">
          <Title as="h2" size="400" weight="medium">
            <Trans id="settings.organization.data-catalogs.products.details.sizes">Sizes</Trans>
          </Title>

          {isEditing ? (
            <SaveCancelButtons
              onSave={handleSave}
              onCancel={handleCancel}
              isSaving={isUpdateProductPending}
            />
          ) : (
            <EditButton
              label={t({
                id: 'settings.organization.data-catalogs.products.details.sizes.edit',
                message: 'Edit product sizes',
              })}
              onClick={() => setIsEditing(true)}
            />
          )}
        </Stack>

        {isEditing ? (
          <>
            <ActivateSizesToggle
              product={product}
              sizes={sizes}
              isDeleteSizesModalOpen={isDeleteSizesModalOpen}
              setSizes={setSizes}
              setIsDeleteSizesModalOpen={setIsDeleteSizesModalOpen}
            />

            {(!!sizes?.length || sizes === null) && (
              <>
                <SizesInputMultiSelect sizes={sizes} setSizes={setSizes} showError={showError} />
                <AddValueButton
                  label={t({
                    id: 'settings.organization.data-catalogs.products.details.sizes.add-size',
                    message: 'Add size',
                  })}
                  isDisabled={isUpdateProductPending}
                  onPress={() => setSizes([...(sizes ?? []), { fr: '', en: '', ja: '' }])}
                />
              </>
            )}
          </>
        ) : (
          <SizesReadonly product={product} />
        )}
      </Stack>
    </Box>
  );
};

const SizesReadonly = ({ product }: { product: Product }) => {
  const sizes = product.sizes;

  return (
    <Stack>
      {sizes?.length ? (
        <SizesList sizes={sizes} />
      ) : sizes === null ? (
        <Trans id="settings.organization.data-catalogs.products.details.sizes.no-sizes-added">
          No sizes have been added yet.
        </Trans>
      ) : (
        <Trans id="settings.organization.data-catalogs.products.details.sizes.product-has-no-sizes">
          This product has no sizes.
        </Trans>
      )}
    </Stack>
  );
};

const SizesList = ({ sizes }: { sizes: I18nWording[] }) => {
  const { i18n } = useLingui();

  const userLocale = i18n.locale as Locale;

  return sizes.map((size) => size[userLocale]).join(', ');
};

const ActivateSizesToggle = ({
  product,
  sizes,
  isDeleteSizesModalOpen,
  setSizes,
  setIsDeleteSizesModalOpen,
}: {
  product: Product;
  sizes: I18nWording[] | null;
  isDeleteSizesModalOpen: boolean;
  setSizes: (sizes: I18nWording[] | null) => void;
  setIsDeleteSizesModalOpen: (isOpen: boolean) => void;
}) => {
  const { t } = useLingui();

  return (
    <>
      <Toggle
        label={t({
          id: 'settings.organization.data-catalogs.products.details.sizes.activate-sizes',
          message: 'Activate sizes on this product',
        })}
        alignment="left"
        isSelected={!!sizes?.length || sizes === null}
        onChange={(value) => {
          if (value === true) {
            setSizes(null);
          } else {
            if (sizes?.length || product.sizes) {
              setIsDeleteSizesModalOpen(true);
            } else {
              setSizes([]);
            }
          }
        }}
      />

      <DeleteSizesDialog
        isOpen={isDeleteSizesModalOpen}
        onConfirm={() => {
          setSizes([]);
          setIsDeleteSizesModalOpen(false);
        }}
        onCancel={() => {
          setIsDeleteSizesModalOpen(false);
        }}
      />
    </>
  );
};

const SizesInputMultiSelect = ({
  sizes,
  setSizes,
  showError,
}: {
  sizes: I18nWording[] | null;
  setSizes: (sizes: I18nWording[] | null) => void;
  showError: boolean;
}) => {
  const { t } = useLingui();

  const updateSize = (index: number, newSize: I18nWording) => {
    if (sizes) {
      setSizes(sizes.map((size, i) => (i === index ? newSize : size)));
    }
  };

  return (
    <Stack gap="0.5rem">
      {sizes?.map((size, index) => (
        <Stack row gap="0.5rem" key={index}>
          <TranslatableInputText
            aria-label={t({
              id: 'settings.organization.data-catalogs.products.details.sizes.size',
              message: 'Size',
            })}
            placeholder={t({
              id: 'settings.organization.data-catalogs.products.details.sizes.placeholder',
              message: 'Enter a size',
            })}
            size="small"
            wrapperStyle={{ flex: 1 }}
            value={size}
            onChange={(value) => updateSize(index, value)}
            isInvalid={(!size.en || !size.fr) && showError}
          />
          <DeleteButtonIconOnly
            ariaLabel={t({
              id: 'settings.organization.data-catalogs.products.details.sizes.size.delete',
              message: 'Delete size',
            })}
            size="medium"
            onClick={() => {
              const filteredSizes = sizes.filter((_size, _index) => _index !== index);

              setSizes(filteredSizes.length ? filteredSizes : null);
            }}
          />
        </Stack>
      ))}

      {showError && (
        <Message type="error">
          <Trans id="settings.organization.data-catalogs.products.details.sizes.missing-size">
            Please add at least a translation for EN and FR languages
          </Trans>
        </Message>
      )}
    </Stack>
  );
};

const DeleteSizesDialog = ({
  isOpen,
  onConfirm,
  onCancel,
}: {
  isOpen: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) => {
  return (
    <Dialog
      isOpen={isOpen}
      onOpenChange={onCancel}
      title={
        <Trans id="settings.organization.data-catalogs.products.details.sizes.delete-sizes-dialog.title">
          All sizes are going to be deleted
        </Trans>
      }
      style={{ maxWidth: '25rem' }}
    >
      <main>
        <Paragraph>
          <Trans id="settings.organization.data-catalogs.products.details.sizes.delete-sizes-dialog.content">
            All sizes of this product will be deleted.
            <br />
            Are you sure you want to continue?
          </Trans>
        </Paragraph>
      </main>

      <Footer>
        <FooterButtons>
          <Button variant="secondary" size="medium" onPress={onCancel}>
            <Trans id="settings.organization.data-catalogs.products.details.sizes.delete-sizes-dialog.cancel">
              Cancel
            </Trans>
          </Button>
          <Button variant="danger" size="medium" onPress={onConfirm}>
            <Trans id="settings.organization.data-catalogs.products.details.sizes.delete-sizes-dialog.content.confirm">
              Confirm
            </Trans>
          </Button>
        </FooterButtons>
      </Footer>
    </Dialog>
  );
};
